import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueuedPopup } from "@/hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import { useIsHomeDisplayReady } from "@/modules/homeDisplay/hooks";
import { useHomeDisplayStore } from "@/modules/homeDisplay/stores";
import { useUserDataFlattened } from "@/modules/user/stores";
import { ROUTES } from "@/types/routes";
import { markCashbackBoostRead } from "@/api/cashbackBoost";
import { useNavigation } from "@/utils/navigation";
import type { CashbackBoostPopupItem } from "./types";
import { closeDepositPopup } from "@/components/DepositPopup/triggerDepositPopup";

// 到账弹窗客户端「已读」守卫(按 userId 分桶,sessionStorage):
// 防止 markRead 服务端生效前的 refresh 竞态把已读项写回配置,导致同一条到账弹窗重复弹出。
const cbbPopupReadKey = (userId?: string) => `cbb_popup_read_ids_${userId ?? "_"}`;
function loadCbbPopupReadIds(userId?: string): Set<string> {
  try {
    const raw = sessionStorage.getItem(cbbPopupReadKey(userId));
    return new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set<string>();
  }
}
function addCbbPopupReadId(userId: string | undefined, id: string): void {
  try {
    const set = loadCbbPopupReadIds(userId);
    set.add(id);
    sessionStorage.setItem(cbbPopupReadKey(userId), JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

const CashbackBoostCongratsDialog = lazy(() =>
  import("./CashbackBoostCongratsDialog").then(m => ({
    default: m.CashbackBoostCongratsDialog,
  }))
);

const POPUP_CFG = POPUP_CONFIGS.CASHBACK_BOOST_CONGRATS;

function pickNextPopup(
  list: CashbackBoostPopupItem[] | undefined
): CashbackBoostPopupItem | null {
  if (!list?.length) return null;
  const now = Date.now();
  const valid = list.filter(p => (p.expireAt ?? 0) > now);
  if (!valid.length) return null;
  return [...valid].sort((a, b) => a.expireAt - b.expireAt)[0];
}

const HomeCashbackBoostPopupHost: React.FC<{
  popup: CashbackBoostPopupItem | null;
  onComplete: () => void;
  onRefresh: () => Promise<void>;
  onRead: (recordId: string) => void;
}> = ({ popup, onComplete, onRefresh, onRead }) => {
  const { navigate } = useNavigation();

  // 到账弹窗一显示,就关掉底下的 Deposit 充值弹窗:
  // DepositPopup 是独立全局弹窗、不在弹窗队列里,且 /recharge→HOME 前后 path 都是 HOME,
  // 其"path 变化"关闭条件不触发,故此处主动派发关闭事件,避免到账弹窗盖在 deposit 上。
  useEffect(() => {
    closeDepositPopup();
  }, []);

  const handleClose = async () => {
    // onRead 内已幂等地置守卫 + 调 mark_read(见 hook markRead),且同步先于任何 await:
    // 即使 handleClose 被二次触发,也不会重复 mark_read / 二次弹出。
    if (popup?.recordId) onRead(popup.recordId);
    await onRefresh();
    onComplete();
  };

  const handleClaim = async () => {
    if (popup?.recordId) onRead(popup.recordId);
    await onRefresh();
    onComplete();
    navigate(`${ROUTES.VIP}?tabIndex=1`);
  };

  return (
    <CashbackBoostCongratsDialog
      visible={!!popup}
      record={popup}
      onClose={handleClose}
      onClaim={handleClaim}
    />
  );
};

export const useCashbackBoostPopup = () => {
  const isConfigReady = useIsHomeDisplayReady();
  const {
    isAuthenticated,
    data: userInfo,
    loadUserRealtime,
  } = useUserDataFlattened();
  const popups = useHomeDisplayStore(s => s.data?.cashbackBoostPopups);
  const refreshConfig = useHomeDisplayStore(s => s.refreshConfig);

  const userId = userInfo?.userId as string | undefined;
  const readIdsRef = useRef<Set<string>>(new Set());
  const [readVersion, setReadVersion] = useState(0);

  // 载入该用户已持久化的已读集(切换用户时重载)
  useEffect(() => {
    readIdsRef.current = loadCbbPopupReadIds(userId);
    setReadVersion(v => v + 1);
  }, [userId]);

  const markRead = useCallback(
    (recordId: string) => {
      // 幂等:同一条只处理一次。handleClose 若被二次触发,这里直接返回,
      // 不再发第二次 mark_read、也不再二次弹(线上实测同一次关闭 mark_read 发了 2 次)。
      if (!recordId || readIdsRef.current.has(recordId)) return;
      readIdsRef.current.add(recordId); // 同步置守卫,先于任何 await
      addCbbPopupReadId(userId, recordId);
      setReadVersion(v => v + 1);
      // 服务端标记已读也在此发一次(天然去重),取代原 handleClose 里的直接调用
      void markCashbackBoostRead(recordId).catch(() => {
        /* ignore */
      });
    },
    [userId]
  );

  // 先滤掉已读 recordId 再挑下一个:即便 refresh 竞态把已读项写回配置,也不会重复弹出。
  // readVersion 用于在标记已读后强制重算(readIdsRef 是 ref,不触发 memo)。
  const nextPopup = useMemo(() => {
    const unread = (popups ?? []).filter(
      p => p.recordId && !readIdsRef.current.has(p.recordId)
    );
    return pickNextPopup(unread);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popups, readVersion]);

  // 入队闸门必须【同步】读 readIdsRef(而非异步的 memo nextPopup):
  // 否则 markRead 后、readVersion 尚未传播时,refresh 竞态把已读项写回 → 队列基于旧值又入队 → 到账弹窗"闪一下"。
  const shouldShowFn = useCallback(() => {
    if (!isConfigReady || !isAuthenticated) return false;
    const unread = (popups ?? []).filter(
      p => p.recordId && !readIdsRef.current.has(p.recordId)
    );
    return pickNextPopup(unread) != null;
  }, [isConfigReady, isAuthenticated, popups]);

  useQueuedPopup({
    popupId: POPUP_CFG.id,
    priority: POPUP_CFG.priority,
    shouldShow: shouldShowFn,
    renderContent: onComplete => (
      <Suspense fallback={null}>
        <HomeCashbackBoostPopupHost
          popup={nextPopup}
          onComplete={onComplete}
          onRead={markRead}
          onRefresh={async () => {
            try {
              await refreshConfig();
              await loadUserRealtime();
            } catch {
              /* ignore */
            }
          }}
        />
      </Suspense>
    ),
    showOnce: false,
    allowManualTrigger: false,
    autoTrigger: true,
    dependencies: [isConfigReady, isAuthenticated, nextPopup?.recordId],
    allowedPaths: [ROUTES.HOME, ROUTES.HOME_ALT, "/"],
    blockedPaths: [
      ROUTES.LOGIN,
      ROUTES.REGISTER,
      ROUTES.RECHARGE,
      ROUTES.WITHDRAW,
    ],
    userBinding: true,
    getUserId: () => userInfo?.userId as string | undefined,
  });
};
